/**
 * BlastRadiusV2 (spec §7) — circular domain-sector graph.
 *
 * N domain sectors arranged in a circle at R_CLUSTER radius. Entities within
 * each sector in a radial fan. Dashed ellipse hulls per domain. CSS @keyframes
 * propagation rings per written entity (150ms delay/hop). Quadratic Bezier
 * edges for sequential hop writes. Ghost nodes at 15% opacity. Pure SVG, no D3.
 */
import { useState, useMemo } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import type { EnrichedSimulationTrace } from '../state/simulator.js';
import { DomainBadge } from './DomainBadge.js';

export interface BlastRadiusV2Props {
  manifest: AtlasManifest;
  trace: EnrichedSimulationTrace;
}

const SVG_W = 900;
const SVG_H = 900;
const H_BODY = 860;
const CX = 450;
const CY = 430;
const R_CLUSTER = 300;
const ENTITY_R = 8;
const MAX_FAN_PER_ROW = 4;
const FAN_R_BASE = 28;
const FAN_R_STEP = 26;

const DOMAIN_COLOR_MAP: Record<string, string> = {
  '--d-investments': '#60A5FA',
  '--d-budgets': '#818CF8',
  '--d-bills': '#F472B6',
  '--d-cashflow': '#34D399',
  '--d-transactions': '#FBBF24',
  '--d-accounts': '#A78BFA',
  '--d-goals': '#FB923C',
  '--d-reports': '#22D3EE',
  '--d-tax': '#F87171',
  '--d-connections': '#4ADE80',
  '--d-migration': '#E879F9',
  '--d-features': '#FCD34D',
  '--d-settings': '#94A3B8',
  '--d-security': '#F97316',
  '--d-subscription': '#C084FC',
};

const FALLBACK_COLORS = [
  '#60A5FA', '#818CF8', '#F472B6', '#34D399', '#FBBF24',
  '#A78BFA', '#FB923C', '#22D3EE', '#F87171', '#4ADE80',
  '#E879F9', '#FCD34D', '#94A3B8', '#F97316', '#C084FC',
  '#67E8F9', '#86EFAC', '#FCA5A5', '#FDE68A', '#C4B5FD',
  '#6EE7B7', '#FDBA74',
];

function getDomainColor(colorToken: string, fallbackIndex: number): string {
  return DOMAIN_COLOR_MAP[colorToken] ?? FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length];
}

interface EntityPos {
  entityId: string;
  x: number;
  y: number;
  color: string;
  hop?: number;
}

type DomainEntitiesMap = Map<string, AtlasManifest['entities']>;

function computeEntityPositions(
  manifest: AtlasManifest,
  writtenEntities: Map<string, number>,
  domainEntitiesMap: DomainEntitiesMap,
): Map<string, EntityPos> {
  const posMap = new Map<string, EntityPos>();
  const N = manifest.domains.length;

  manifest.domains.forEach((domain, di) => {
    const angle = (di / N) * 2 * Math.PI - Math.PI / 2;
    const dcX = CX + R_CLUSTER * Math.cos(angle);
    const dcY = CY + R_CLUSTER * Math.sin(angle);
    const color = getDomainColor(domain.color, di);
    const entities = domainEntitiesMap.get(domain.id) ?? [];
    const numCols = Math.min(entities.length, MAX_FAN_PER_ROW);
    const sectorSpread = Math.min((2 * Math.PI / N) * 0.6, Math.PI / 2.5);

    entities.forEach((entity, ei) => {
      const fanRow = Math.floor(ei / MAX_FAN_PER_ROW);
      const fanCol = ei % MAX_FAN_PER_ROW;
      const fanR = FAN_R_BASE + fanRow * FAN_R_STEP;
      const colOffset = numCols > 1 ? (fanCol - (numCols - 1) / 2) : 0;
      const colAngle = colOffset * (sectorSpread / Math.max(numCols - 1, 1));
      const entityAngle = angle + colAngle;

      posMap.set(entity.id, {
        entityId: entity.id,
        x: dcX + fanR * Math.cos(entityAngle),
        y: dcY + fanR * Math.sin(entityAngle),
        color,
        hop: writtenEntities.get(entity.id),
      });
    });
  });

  return posMap;
}

export function BlastRadiusV2({ manifest, trace }: BlastRadiusV2Props): React.JSX.Element {
  const [hovered, setHovered] = useState<string | null>(null);
  const N = manifest.domains.length;

  // Precomputed once per manifest — avoids O(domains × entities) filter on every hover re-render
  const domainEntitiesMap = useMemo<DomainEntitiesMap>(() => {
    const map: DomainEntitiesMap = new Map();
    for (const domain of manifest.domains) {
      map.set(domain.id, manifest.entities.filter((e) => e.domain === domain.id));
    }
    return map;
  }, [manifest]);

  const writtenEntities = useMemo(() => {
    const map = new Map<string, number>();
    for (const step of trace.steps) {
      for (const entityId of step.writes) {
        if (!map.has(entityId)) map.set(entityId, step.hop);
      }
    }
    return map;
  }, [trace.steps]);

  const entityPos = useMemo(
    () => computeEntityPositions(manifest, writtenEntities, domainEntitiesMap),
    [manifest, writtenEntities, domainEntitiesMap],
  );

  // Quadratic Bezier edges between sequential step writes
  const edges = useMemo(() => {
    const result: Array<{ x1: number; y1: number; x2: number; y2: number; color: string }> = [];
    for (let si = 0; si < trace.steps.length - 1; si++) {
      for (const fromId of trace.steps[si].writes) {
        for (const toId of trace.steps[si + 1].writes) {
          const from = entityPos.get(fromId);
          const to = entityPos.get(toId);
          if (from && to) {
            result.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y, color: from.color });
          }
        }
      }
    }
    return result;
  }, [trace.steps, entityPos]);

  return (
    <div className="blast-radius-v2" aria-label={`Blast radius for: ${trace.action.label}`}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
        {manifest.domains.map((domain) => (
          <DomainBadge key={domain.id} domainId={domain.id} size="sm" />
        ))}
      </div>
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: '100%', height: SVG_H }}>
        {/* CSS @keyframes for propagation rings */}
        <style>{`
          @keyframes blast-ring-pulse {
            0% { transform: scale(1); opacity: 0.5; }
            100% { transform: scale(4.5); opacity: 0; }
          }
          .blast-ring {
            animation: blast-ring-pulse 1.6s ease-out infinite;
            transform-box: fill-box;
            transform-origin: center;
          }
        `}</style>

        {/* Bezier edges for sequential hop writes */}
        {edges.map((e, i) => {
          const cpx = (e.x1 + e.x2) / 2 * 0.6 + CX * 0.4;
          const cpy = (e.y1 + e.y2) / 2 * 0.6 + CY * 0.4;
          return (
            <path
              key={`edge-${i}`}
              d={`M ${e.x1},${e.y1} Q ${cpx},${cpy} ${e.x2},${e.y2}`}
              fill="none"
              stroke={e.color}
              strokeWidth={1}
              opacity={0.4}
              className="blast-edge"
            />
          );
        })}

        {/* Domain sectors */}
        {manifest.domains.map((domain, di) => {
          const angle = (di / N) * 2 * Math.PI - Math.PI / 2;
          const dcX = CX + R_CLUSTER * Math.cos(angle);
          const dcY = CY + R_CLUSTER * Math.sin(angle);
          const color = getDomainColor(domain.color, di);
          const entities = domainEntitiesMap.get(domain.id) ?? [];

          const maxRow = entities.length > 0 ? Math.floor((entities.length - 1) / MAX_FAN_PER_ROW) : 0;
          const fanMaxR = FAN_R_BASE + maxRow * FAN_R_STEP;
          const hullRx = Math.max(60, fanMaxR + 22);
          const hullRy = Math.max(50, fanMaxR + 18);

          // Label positioned outward from domain cluster center; clamped so it never clips SVG edge
          const labelAngle = Math.atan2(dcY - CY, dcX - CX);
          const labelDist = hullRy + 18;
          const lx = dcX + labelDist * Math.cos(labelAngle);
          const ly = Math.max(14, Math.min(SVG_H - 14, dcY + labelDist * Math.sin(labelAngle)));

          return (
            <g key={domain.id} className="blast-domain-sector">
              {/* Dashed ellipse hull */}
              <ellipse
                cx={dcX}
                cy={dcY}
                rx={hullRx}
                ry={hullRy}
                fill="none"
                stroke={color}
                strokeWidth={1}
                strokeDasharray="6,4"
                opacity={0.35}
              />

              {/* Domain label */}
              <text
                x={lx}
                y={ly + 4}
                textAnchor="middle"
                fontSize={10}
                fill={color}
                fontWeight={600}
                className="blast-domain-label"
              >
                {domain.name.length > 16 ? `${domain.name.slice(0, 15)}…` : domain.name}
              </text>

              {/* Entity circles */}
              {entities.map((entity) => {
                const pos = entityPos.get(entity.id);
                if (!pos) return null;
                const isWritten = pos.hop !== undefined;
                const isHovered = hovered === entity.id;

                return (
                  <g
                    key={entity.id}
                    className="blast-entity"
                    onMouseEnter={() => setHovered(entity.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Propagation ring (CSS @keyframes, 150ms delay/hop) */}
                    {isWritten && (
                      <circle
                        cx={pos.x}
                        cy={pos.y}
                        r={ENTITY_R + 4}
                        fill="none"
                        stroke={color}
                        strokeWidth={1.5}
                        className="blast-ring"
                        style={{ animationDelay: `${(pos.hop ?? 0) * 150}ms` }}
                      />
                    )}

                    {/* Entity circle */}
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r={ENTITY_R}
                      fill={isWritten ? color : 'var(--surface3)'}
                      stroke={color}
                      strokeWidth={isWritten ? 0 : 1}
                      strokeDasharray={isWritten ? undefined : '3,3'}
                      opacity={isWritten ? 0.9 : 0.15}
                      className="blast-entity-circle"
                    />

                    {/* Hop number inside written circles */}
                    {isWritten && (
                      <text
                        x={pos.x}
                        y={pos.y + 3}
                        textAnchor="middle"
                        fontSize={7}
                        fill="#fff"
                        fontWeight={700}
                        aria-hidden
                      >
                        {(pos.hop ?? 0) + 1}
                      </text>
                    )}

                    {/* Hover label */}
                    {isHovered && (
                      <text
                        x={pos.x + ENTITY_R + 4}
                        y={pos.y + 3}
                        fontSize={9}
                        fill="var(--text1)"
                        className="blast-hover-label"
                      >
                        {entity.id}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Legend */}
        <g transform={`translate(8, ${H_BODY + 10})`}>
          <circle cx={6} cy={0} r={6} fill="var(--text2)" opacity={0.85} />
          <text x={16} y={4} fontSize={9} fill="var(--text3)">written entity</text>
          <circle
            cx={104}
            cy={0}
            r={6}
            fill="var(--surface3)"
            stroke="var(--text3)"
            strokeWidth={0.75}
            strokeDasharray="3,3"
            opacity={0.15}
          />
          <text x={114} y={4} fontSize={9} fill="var(--text3)">untouched</text>
          <text x={200} y={4} fontSize={9} fill="var(--text3)">ring = hop depth</text>
        </g>
      </svg>
    </div>
  );
}
